import { useEffect, useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate, useParams, Link } from "react-router-dom"
import { MessageSquare, ThumbsUp, CheckCircle2, Clock, Search, ArrowLeft } from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Badge } from "@/clientWeb/components/ui/badge"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { SkeletonList } from "@/clientWeb/components/ui/page-loader"
import { webinarAnalyticsService, type WebinarQAItem } from "../services/webinar-analytics.service"

export default function QAPage() {
  const t = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [filter, setFilter] = useState<"all" | "answered" | "pending" | "dismissed">("all")
  const [items, setItems] = useState<WebinarQAItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    webinarAnalyticsService.listQA(id ?? '')
      .then((data) => { if (!cancelled) setItems(data) })
      .catch((err) => {
        if (cancelled) return
        toast.error({ title: 'Failed to load Q&A', description: err instanceof Error ? err.message : 'Please try again' })
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id])

  const filtered = items
    .filter(q => filter === "all" || q.status === filter)
    .filter(q => !search || q.question.toLowerCase().includes(search.toLowerCase()) || q.askedBy.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => b.upvotes - a.upvotes)

  const answeredCount  = items.filter(q => q.status === "answered").length
  const pendingCount   = items.filter(q => q.status === "pending").length
  const dismissedCount = items.filter(q => q.status === "dismissed").length

  if (loading) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden bg-background p-4">
        <SkeletonList rows={5} />
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden bg-background">
      {/* Header */}
      <div className="px-4 sm:px-6 py-3 border-b border-stroke-100 dark:border-border bg-card shrink-0">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
          <Link to="/webinar" className="text-primary font-semibold hover:text-primary transition-colors">{t("webinar.webinar")}</Link>
          <span>/</span>
          <Link to={`/webinar/${id}`} className="text-primary font-semibold hover:text-primary transition-colors">{t("webinar.details")}</Link>
          <span>/</span>
          <Link to={`/webinar/${id}/analytics`} className="text-primary font-semibold hover:text-primary transition-colors">{t("webinar.analytics")}</Link>
          <span>/</span>
          <span className="text-foreground font-semibold">{t("webinar.qa")}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => navigate(`/webinar/${id}/analytics`)}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-base font-bold text-foreground">{t("webinar.qaResponses")}</h1>
              <p className="text-xs text-muted-foreground">{items.length} {t("webinar.totalQuestions")}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: t("webinar.answered"),  value: answeredCount,  iconBg: "bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20" },
            { label: t("webinar.pending"),   value: pendingCount,   iconBg: "bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20" },
            { label: t("webinar.dismissed"), value: dismissedCount, iconBg: "bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800/50 dark:to-slate-800/30" },
          ].map(({ label, value, iconBg }) => (
            <div key={label} className="group relative bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl px-4 py-4 shadow-xs overflow-hidden text-left">
              <div className={`absolute inset-0 ${iconBg}`}>
                <svg className="absolute bottom-0 right-0 w-full h-full" viewBox="0 0 200 120" fill="none" preserveAspectRatio="none">
                  <path d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z" fill="currentColor" className="text-white/30 dark:text-white/10" />
                </svg>
              </div>
              <div className="relative z-10">
                <div className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white mb-1">{value}</div>
                <div className="text-sm font-medium text-slate-600 dark:text-slate-400">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Search + Filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 bg-muted/50 rounded-xl px-3 py-2 flex-1 min-w-48">
            <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t("webinar.searchQuestions")}
              className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex items-center bg-muted rounded-lg p-1 gap-1">
            {(["all", "answered", "pending", "dismissed"] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${filter === f ? "bg-card shadow-sm text-primary" : "text-muted-foreground"}`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1) === "All" ? t("webinar.all") : f.charAt(0).toUpperCase() + f.slice(1) === "Answered" ? t("webinar.answered") : f.charAt(0).toUpperCase() + f.slice(1) === "Pending" ? t("webinar.pending") : t("webinar.dismissed")}
              </button>
            ))}
          </div>
        </div>

        {/* Questions list */}
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center gap-3 bg-card rounded-xl border border-border">
              <MessageSquare className="w-10 h-10 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">{t("webinar.noQuestionsFound")}</p>
            </div>
          ) : (
            filtered.map(q => (
              <div key={q.id} className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Upvote */}
                    <div className="flex flex-col items-center gap-0.5 shrink-0 w-10">
                      <ThumbsUp className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-bold text-foreground">{q.upvotes}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <p className="text-sm font-medium text-foreground leading-snug">{q.question}</p>
                        <Badge className={`text-[10px] shrink-0 font-semibold ${
                          q.status === "answered"  ? "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
                        : q.status === "pending"   ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
                        : "bg-muted text-muted-foreground border-border"}`}
                        >
                          {q.status === "answered" ? <><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />{t("webinar.answered")}</>
                           : q.status === "pending" ? <><Clock className="w-2.5 h-2.5 mr-0.5" />{t("webinar.pending")}</>
                           : t("webinar.dismissed")}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium">{q.askedBy}</span>
                        <span>·</span>
                        <span>{q.time}</span>
                      </div>
                    </div>
                  </div>

                  {/* Answer */}
                  {q.answer && (
                    <div className="mt-3 ml-13 pl-4 border-l-2 border-primary/30">
                      <div className="flex items-center gap-1.5 mb-1">
                        <CheckCircle2 className="w-3 h-3 text-primary" />
                        <span className="text-[11px] font-semibold text-primary">{q.answeredBy} {t("webinar.answered")}</span>
                      </div>
                      <p className="text-sm text-foreground/80 leading-relaxed">{q.answer}</p>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
